/**
 * GS1 mod-10 check-digit computation used to CONSTRUCT fixture barcodes with
 * correct check digits at module-load time.
 *
 * Deliberately a second, local implementation: the fixture-integrity tests
 * RE-CHECK every constructed barcode against the independent validators in
 * `features/shared/scanner` (`src/symbology.ts`), so a bug in either
 * implementation fails the suite instead of cancelling out.
 */

const DIGITS_RE = /^[0-9]+$/;

/**
 * GS1 mod-10 check digit for a payload (digits WITHOUT the check digit).
 * Weights 3/1 anchor at the check-digit position and walk right-to-left —
 * one algorithm serves EAN-13, UPC-A and GTIN-14.
 */
export function gs1CheckDigit(payloadDigits: string): number {
  if (!DIGITS_RE.test(payloadDigits)) {
    throw new RangeError(`gs1CheckDigit expects digits, got ${JSON.stringify(payloadDigits)}`);
  }
  let sum = 0;
  for (let i = 0; i < payloadDigits.length; i++) {
    const digit = payloadDigits.charCodeAt(payloadDigits.length - 1 - i) - 48;
    sum += digit * (i % 2 === 0 ? 3 : 1);
  }
  return (10 - (sum % 10)) % 10;
}

function withCheckDigit(payload: string, payloadLength: number, name: string): string {
  if (payload.length !== payloadLength || !DIGITS_RE.test(payload)) {
    throw new RangeError(
      `${name} payload must be ${payloadLength} digits, got ${JSON.stringify(payload)}`,
    );
  }
  return payload + String(gs1CheckDigit(payload));
}

/** Build a full 13-digit EAN-13 from its 12-digit payload. */
export function ean13(payload12: string): string {
  return withCheckDigit(payload12, 12, "EAN-13");
}

/** Build a full 12-digit UPC-A from its 11-digit payload. */
export function upcA(payload11: string): string {
  return withCheckDigit(payload11, 11, "UPC-A");
}

/** Build a full 14-digit GTIN-14 from its 13-digit payload. */
export function gtin14(payload13: string): string {
  return withCheckDigit(payload13, 13, "GTIN-14");
}
